// packages/shared-utils/src/main/deviceConnectionUtils.ts
import { deviceMatchesActiveBrand } from "./brandDeviceFilter";
import { getSupportedDevicesSync } from "./supportedDevices";
import { hasDeviceLinkConfig, getDeviceLinkConfig } from "./deviceLinkUrl";

/** 配置表是否声明了第三方跳转地址（键盘/鼠标通用，与 solutionRovider 无关） */
export function hasDeviceExternalUrl(
  configuration:
    | {
        url?: string | null;
        link?: import("./deviceLink.types").DeviceLinkConfig;
      }
    | null
    | undefined
): boolean {
  if (hasDeviceLinkConfig(configuration)) return true;
  return Boolean(configuration?.url && String(configuration.url).trim());
}

const normDeviceName = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

/**
 * 同 VID/PID 的第三方 url 设备：HID productName 须与配置表 productName / displayname 对应。
 * 例如 DEEP80 Pro 与 DEEP68 共用 0x3151:0x5029 时靠名称区分。
 */
export function externalUrlHidProductNameMatches(
  hidProductName: unknown,
  configuration: {
    productName?: string | null;
    displayname?: string | null;
  }
): boolean {
  const hidPn = normDeviceName(hidProductName);
  const cfgPn = normDeviceName(configuration?.productName);
  const disp = normDeviceName(configuration?.displayname);
  if (!cfgPn && !disp) return true;
  if (!hidPn) return false;
  if (
    cfgPn &&
    (hidPn === cfgPn || hidPn.includes(cfgPn) || cfgPn.includes(hidPn))
  ) {
    return true;
  }
  if (
    disp &&
    (hidPn === disp || hidPn.includes(disp) || disp.includes(hidPn))
  ) {
    return true;
  }
  return false;
}

/** 按 VID/PID 在配置表中查找设备行（先 keyboard 后 mouse） */
export function findSupportedDeviceByVidPid(
  vendorId: number,
  productId: number
): Record<string, unknown> | undefined {
  for (const listKey of ["keyboard", "mouse"] as const) {
    const list = getSupportedDevicesSync()[listKey] as Record<
      string,
      unknown
    >[];
    const row = list.find(
      (d) =>
        d.vendorId === vendorId &&
        d.productId === productId &&
        deviceMatchesActiveBrand(d)
    );
    if (row) return row;
  }
  return undefined;
}

/** 同 VID/PID 多型号时按 HID productName 精确匹配配置行 */
export function findSupportedDeviceByVidPidAndProductName(
  vendorId: number,
  productId: number,
  hidProductName: unknown
): Record<string, unknown> | undefined {
  const matches: Record<string, unknown>[] = [];
  for (const listKey of ["keyboard", "mouse"] as const) {
    const list = getSupportedDevicesSync()[listKey] as Record<
      string,
      unknown
    >[];
    for (const row of list) {
      if (
        row.vendorId === vendorId &&
        row.productId === productId &&
        deviceMatchesActiveBrand(row)
      ) {
        matches.push(row);
      }
    }
  }
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  const byName = matches.filter((row) =>
    externalUrlHidProductNameMatches(hidProductName, row as any)
  );
  if (byName.length === 1) return byName[0];
  if (byName.length > 1) return byName[0];
  // 同 VID/PID 多外链（如 DEEP80 Pro legacy url + DEEP68 link）：勿取表中第一条外链行
  const linkOnly = matches.filter((row) => getDeviceLinkConfig(row as never));
  if (linkOnly.length === 1) return linkOnly[0];
  const legacyExternal = matches.filter(
    (row) => hasDeviceExternalUrl(row) && !getDeviceLinkConfig(row as never)
  );
  if (legacyExternal.length === 1) return legacyExternal[0];
  return matches[0];
}

/** 展立鼠标同 VID/PID 多型号（如 EXX Alpha7 / EWEADN Z7MAX）须按 HID productName 拆组 */
export function zhanliMouseVidPidKeysNeedingProductNameSplit(): Set<string> {
  const namesByKey = new Map<string, Set<string>>();
  const mice = getSupportedDevicesSync().mouse as Record<string, unknown>[];
  for (const row of mice) {
    if (row.solutionRovider !== "zhanli") continue;
    const vid = row.vendorId;
    const pid = row.productId;
    if (typeof vid !== "number" || typeof pid !== "number") continue;
    const key = `${vid}-${pid}`;
    const pn = normDeviceName(row.productName);
    if (!pn) continue;
    if (!namesByKey.has(key)) namesByKey.set(key, new Set());
    namesByKey.get(key)!.add(pn);
  }
  const out = new Set<string>();
  for (const [key, names] of namesByKey) {
    if (names.size > 1) out.add(key);
  }
  return out;
}

export function zhanliMouseVidPidNeedsProductNameSplit(
  vidPidKey: string
): boolean {
  return zhanliMouseVidPidKeysNeedingProductNameSplit().has(vidPidKey);
}

/** 是否为配置了 url 的第三方跳转设备 */
export function isExternalUrlSupportedVidPid(
  vendorId: number,
  productId: number
): boolean {
  const row = findSupportedDeviceByVidPid(vendorId, productId);
  return hasDeviceExternalUrl(row);
}

type FreqLinkSchemeHint = "a" | "c" | unknown;

/**
 * 富芮坤 C 方案：设备上报电量恒为 100% 假数据，驱动内不展示电量。
 */
export function isFreqCBatteryUnavailable(
  configuration?: {
    solutionRovider?: unknown;
    freqLinkHint?: FreqLinkSchemeHint;
  } | null,
  deviceInfo?: {
    mouseCfg?: { freqLinkScheme?: FreqLinkSchemeHint };
  } | null
): boolean {
  const sol = String(configuration?.solutionRovider ?? "");
  if (sol === "freqC") return true;
  if (configuration?.freqLinkHint === "c") return true;
  if (String(deviceInfo?.mouseCfg?.freqLinkScheme ?? "") === "c") return true;
  return false;
}

/**
 * SDCX 有线键盘（如 DK63 Machine）：协议 battery 字段无实际意义，不展示电量。
 */
export function isSdcxBatteryUnavailable(
  configuration?: { solutionRovider?: unknown } | null
): boolean {
  return String(configuration?.solutionRovider ?? "") === "SDCX";
}

/**
 * 鼠标连接类型枚举
 * 对应 HID 协议中的 type 字段
 */
export enum MouseConnectionType {
  /** Dongle 1K 模式 */
  DONGLE_1K = 0,
  /** Dongle 4K 模式 */
  DONGLE_4K = 1,
  /** 有线 1K 模式 */
  WIRED_1K = 2,
  /** 有线 8K 模式 */
  WIRED_8K = 3,
  /** Dongle 2K 模式 */
  DONGLE_2K = 4,
  /** Dongle 8K 模式 */
  DONGLE_8K = 5,
}

/**
 * 判断是否为有线连接
 * @param type 连接类型
 * @returns 是否为有线连接
 */
export function isWiredConnection(type: number | undefined): boolean {
  return (
    type === MouseConnectionType.WIRED_1K ||
    type === MouseConnectionType.WIRED_8K
  );
}

/**
 * 判断是否为无线连接
 * @param type 连接类型
 * @returns 是否为无线连接
 */
export function isWirelessConnection(type: number | undefined): boolean {
  return (
    type === MouseConnectionType.DONGLE_1K ||
    type === MouseConnectionType.DONGLE_4K ||
    type === MouseConnectionType.DONGLE_2K ||
    type === MouseConnectionType.DONGLE_8K
  );
}

/**
 * 根据连接类型获取最大报告率
 * @param type 连接类型
 * @returns 最大报告率（Hz）
 */
export function getMaxReportRate(type: number | undefined): number {
  switch (type) {
    case MouseConnectionType.DONGLE_1K:
    case MouseConnectionType.WIRED_1K:
      return 1000;
    case MouseConnectionType.DONGLE_2K:
      return 2000;
    case MouseConnectionType.DONGLE_4K:
      return 4000;
    case MouseConnectionType.WIRED_8K:
    case MouseConnectionType.DONGLE_8K:
      return 8000;
    default:
      return 1000;
  }
}

/**
 * 根据连接类型获取图标名称
 * @param type 连接类型
 * @returns 图标名称
 */
export function getConnectionIcon(type: number | undefined): string {
  return isWiredConnection(type) ? "wired" : "wireless";
}

/**
 * 根据连接类型获取连接状态文本（国际化键）
 * @param type 连接类型
 * @param t 国际化函数
 * @returns 连接状态文本
 */
export function getConnectionText(
  type: number | undefined,
  t: (key: string) => string
): string {
  if (type === undefined) {
    return t("device.connection.detectingDevice");
  }
  return isWiredConnection(type)
    ? t("device.connection.wired")
    : t("device.connection.wireless");
}

/**
 * 根据 isWired 字段获取连接图标（用于动态状态）
 * @param isWired 是否有线
 * @returns 图标名称
 */
export function getConnectionIconByWiredState(
  isWired: boolean | undefined
): string {
  if (isWired === undefined) {
    return "wired"; // 默认显示有线图标
  }
  return isWired ? "wired" : "wireless";
}

/**
 * 根据 isWired 字段获取连接状态文本（用于动态状态）
 * @param isWired 是否有线
 * @param t 国际化函数
 * @returns 连接状态文本
 */
export function getConnectionTextByWiredState(
  isWired: boolean | undefined,
  t: (key: string) => string
): string {
  if (isWired === undefined) {
    return t("device.connection.detectingDevice");
  }
  return isWired
    ? t("device.connection.wired")
    : t("device.connection.wireless");
}

/**
 * 鼠标有线/2.4G 推断（与首页 WorkModeAndPower、侧栏 DeviceStatus 同源）。
 * 匠盟用协议 type；其它方案优先 isWired，再 workMode / 配置表 is4G。
 */
export function resolveMouseWiredConnection(params: {
  solutionRovider?: unknown;
  isWired?: boolean;
  type?: number;
  workMode?: string | number | null;
  configIs4G?: boolean;
  configWorkMode?: string | number | null;
}): boolean | undefined {
  if (typeof params.isWired === "boolean") return params.isWired;
  const sol = String(params.solutionRovider ?? "");
  if (sol === "jiangmeng") {
    if (params.type !== undefined) return isWiredConnection(params.type);
    return undefined;
  }
  const wm =
    params.workMode !== undefined && params.workMode !== null
      ? String(params.workMode)
      : params.configWorkMode !== undefined && params.configWorkMode !== null
        ? String(params.configWorkMode)
        : undefined;
  if (wm === "0") return true;
  if (wm === "1" || wm === "2") return false;
  if (params.configIs4G === true) return false;
  if (params.configIs4G === false) return true;
  return undefined;
}

/**
 * 获取连接类型的描述文本
 * @param type 连接类型
 * @returns 连接类型描述
 */
export function getConnectionTypeDescription(type: number | undefined): string {
  switch (type) {
    case MouseConnectionType.DONGLE_1K:
      return "Dongle 1K";
    case MouseConnectionType.DONGLE_2K:
      return "Dongle 2K";
    case MouseConnectionType.DONGLE_4K:
      return "Dongle 4K";
    case MouseConnectionType.DONGLE_8K:
      return "Dongle 8K";
    case MouseConnectionType.WIRED_1K:
      return "Wired 1K";
    case MouseConnectionType.WIRED_8K:
      return "Wired 8K";
    default:
      return "Unknown";
  }
}

/** 华奋达键盘是否为 2.4G（is4G / workMode / 配置表 dongle PID） */
export function isHuafenda24GKeyboard(config: any): boolean {
  if (!config || config.solutionRovider !== "huafenda") return false;
  if (config.is4G === true) return true;
  const wm = config.workMode;
  if (wm !== undefined && wm !== null && String(wm) === "2") return true;

  const pn = normDeviceName(config.productName);
  if (pn.includes("dongle") || pn.includes("2.4g")) return true;

  const pid = Number(config.productId);
  if (Number.isNaN(pid)) return false;
  const dongleRows = (getSupportedDevicesSync().keyboard as any[]).filter(
    (row) =>
      row?.solutionRovider === "huafenda" &&
      String(row.workMode ?? "") === "2" &&
      Number(row.productId) === pid
  );
  return dongleRows.length > 0;
}

/** 智慧无限键盘是否为 2.4G（is4G / workMode / 配置表 dongle PID） */
export function isZhwxWirelessKeyboard(config: any): boolean {
  if (!config || config.solutionRovider !== "ZHWX") return false;
  if (config.is4G === true) return true;
  const wm = config.workMode;
  if (wm !== undefined && wm !== null && String(wm) === "2") return true;

  const vid = Number(config.vendorId);
  const pid = Number(config.productId);
  if (Number.isNaN(vid) || Number.isNaN(pid)) return false;

  const productName = config.productName ? String(config.productName) : "";
  const dongleRows = (getSupportedDevicesSync().keyboard as any[]).filter(
    (row) =>
      row?.solutionRovider === "ZHWX" &&
      row.is4G === true &&
      row.vendorId === vid &&
      row.productId === pid &&
      (!productName ||
        !row.productName ||
        String(row.productName) === productName)
  );
  return dongleRows.length > 0;
}

export type RongyuanKeyboardConnectionMode = "wired" | "wireless" | "bluetooth";

/**
 * 容园机械轴连接模式（WorkMode: 0=USB, 1=2.4G, 2=蓝牙；与华奋达 workMode 编码不同）
 */
export function resolveRongyuanKeyboardConnectionMode(
  config: any
): RongyuanKeyboardConnectionMode {
  if (!config || config.solutionRovider !== "rongyuan") return "wired";

  const connectionType = String(config.connectionType ?? "")
    .trim()
    .toLowerCase();
  if (connectionType === "bluetooth") return "bluetooth";
  if (
    connectionType === "2.4g" ||
    connectionType === "24g" ||
    connectionType === "wireless"
  ) {
    return "wireless";
  }
  if (connectionType === "usb") return "wired";

  if (config.is4G === true) return "wireless";

  const wm = config.workMode;
  if (wm !== undefined && wm !== null) {
    const mode = String(wm);
    if (mode === "1") return "wireless";
    if (mode === "2") return "bluetooth";
    if (mode === "0") return "wired";
  }

  const vid = Number(config.vendorId);
  const pid = Number(config.productId);
  if (!Number.isNaN(vid) && !Number.isNaN(pid)) {
    const dongleRows = (getSupportedDevicesSync().keyboard as any[]).filter(
      (row) =>
        row?.solutionRovider === "rongyuan" &&
        row.is4G === true &&
        row.vendorId === vid &&
        row.productId === pid
    );
    if (dongleRows.length > 0) return "wireless";
  }

  return "wired";
}

/** 容园机械轴是否为 2.4G（is4G / connectionType / dongle PID） */
export function isRongyuanWirelessKeyboard(config: any): boolean {
  return resolveRongyuanKeyboardConnectionMode(config) === "wireless";
}

/** 贝盈键盘是否为 2.4G（is4G / workMode / 配置表 dongle PID） */
export function isBeiyingWirelessKeyboard(config: any): boolean {
  if (!config || config.solutionRovider !== "beiying") return false;
  if (config.is4G === true) return true;
  const wm = config.workMode;
  if (wm !== undefined && wm !== null && String(wm) === "2") return true;

  const vid = Number(config.vendorId);
  const pid = Number(config.productId);
  if (Number.isNaN(vid) || Number.isNaN(pid)) return false;

  const productName = config.productName ? String(config.productName) : "";
  const dongleRows = (getSupportedDevicesSync().keyboard as any[]).filter(
    (row) =>
      row?.solutionRovider === "beiying" &&
      row.is4G === true &&
      row.vendorId === vid &&
      row.productId === pid &&
      (!productName ||
        !row.productName ||
        String(row.productName) === productName)
  );
  return dongleRows.length > 0;
}

/** VX02（Cmedia Xear）：当前仅支持 2.4G 展示，暂不做有线 */
export function isVx02CmediaEarphone(
  config:
    | {
        deviceId?: unknown;
        displayname?: unknown;
        productId?: unknown;
        solutionRovider?: unknown;
        protocol?: unknown;
      }
    | null
    | undefined
): boolean {
  if (!config) return false;
  const isCmedia =
    config.solutionRovider === "cmedia" || config.protocol === "xear";
  if (!isCmedia) return false;
  const did = normDeviceName(config.deviceId);
  if (did === "vx02") return true;
  if (normDeviceName(config.displayname) === "vx02") return true;
  return Number(config.productId) === 0x0380;
}

/**
 * 获取键盘连接状态文本
 * @param config 设备配置 (getSupportedDevicesSync() 中的项)
 * @param t 国际化函数
 * @returns 连接状态文本
 */
export function getKeyboardConnectionText(
  config: any,
  t: (key: string) => string
): string {
  if (!config) return t("device.connection.detectingDevice");

  if (isVx02CmediaEarphone(config)) {
    return t("device.connection.wireless");
  }

  // Huafenda: workMode (0=USB, 1=BT, 2=2.4G)
  if (config.solutionRovider === "huafenda" && config.workMode !== undefined) {
    const mode = String(config.workMode);
    if (mode === "0") return t("device.connection.wired");
    if (mode === "1") return t("device.connection.bluetooth");
    if (mode === "2") return t("device.connection.wireless");
  }

  // 达克 V80/V102：workMode / is4G / productName「2.4G」
  if (config.solutionRovider === "dakai") {
    const mode = String(config.workMode ?? "");
    const pn = String(config.productName ?? "").trim();
    if (mode === "2" || config.is4G === true || pn === "2.4G") {
      return t("device.connection.wireless");
    }
    return t("device.connection.wired");
  }

  // Beiying: is4G / workMode / dongle PID
  if (config.solutionRovider === "beiying") {
    return isBeiyingWirelessKeyboard(config)
      ? t("device.connection.wireless")
      : t("device.connection.wired");
  }

  // 智慧无限：2.4G 接收器配置 is4G / workMode
  if (config.solutionRovider === "ZHWX") {
    return isZhwxWirelessKeyboard(config)
      ? t("device.connection.wireless")
      : t("device.connection.wired");
  }

  // 容园机械轴：connectionType / is4G / dongle PID（WorkMode 0/1/2）
  if (config.solutionRovider === "rongyuan") {
    const mode = resolveRongyuanKeyboardConnectionMode(config);
    if (mode === "wireless") return t("device.connection.wireless");
    if (mode === "bluetooth") return t("device.connection.bluetooth");
    return t("device.connection.wired");
  }

  // Xingshan: 暂时默认为有线
  if (config.solutionRovider === "xingshan") {
    return t("device.connection.wired");
  }

  // SDCX（DK63 等）：仅 USB 有线
  if (config.solutionRovider === "SDCX") {
    return t("device.connection.wired");
  }

  // 默认有线
  return t("device.connection.wired");
}
